import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ArrowLeft, History, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { hsnApiService, HsnCode, HsnCodeCreateRequest } from '../services/hsnApi';
import { taxRateHistoryApiService, TaxRateHistory } from '../services/taxRateHistoryApi';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

function sortTaxRateHistory(history: TaxRateHistory[]): TaxRateHistory[] {
  return [...history].sort((a, b) => {
    const dateA = a.effectiveFrom ? new Date(a.effectiveFrom).getTime() : 0;
    const dateB = b.effectiveFrom ? new Date(b.effectiveFrom).getTime() : 0;
    return dateB - dateA;
  });
}

export function HSNInformationForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const [current, setCurrent] = useState<HsnCode | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [taxRateChangeReason, setTaxRateChangeReason] = useState('');
  const [taxRateHistory, setTaxRateHistory] = useState<TaxRateHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [addHistoryOpen, setAddHistoryOpen] = useState(false);
  const [savingHistory, setSavingHistory] = useState(false);
  const [newHistoryForm, setNewHistoryForm] = useState({
    effectiveFrom: new Date().toISOString().split('T')[0],
    igstRate: 0,
    cgstRate: 0,
    sgstRate: 0,
    cessRate: 0,
    reasonForChange: '',
  });

  const loadTaxRateHistory = async (hsnId: number) => {
    setLoadingHistory(true);
    try {
      const history = await taxRateHistoryApiService.getTaxRateHistoryByHsnId(hsnId);
      setTaxRateHistory(sortTaxRateHistory(history));
    } catch {
      setTaxRateHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const refreshHsnCurrentRates = async (hsnId: string) => {
    const h = await hsnApiService.getHsnCodeById(hsnId);
    setCurrent(h);
    setForm((prev) => ({
      ...prev,
      currentIgstRate: h.currentIgstRate || 0,
      currentCgstRate: h.currentCgstRate || 0,
      currentSgstRate: h.currentSgstRate || 0,
      currentCessRate: h.currentCessRate || 0,
    }));
  };

  const [form, setForm] = useState<HsnCodeCreateRequest>({
    hsnCode: '',
    description: '',
    chapter: '',
    heading: '',
    subHeading: '',
    currentIgstRate: 0,
    currentCgstRate: 0,
    currentSgstRate: 0,
    currentCessRate: 0,
    isActive: true,
    branchId: selectedBranch || 0,
  });

  useEffect(() => {
    if (id && window.location.pathname.includes('/view')) setViewMode(true);
    if (id && window.location.pathname.includes('/edit')) setViewMode(false);
  }, [id]);

  useEffect(() => {
    const init = async () => {
      try {
        if (id) {
          const h = await hsnApiService.getHsnCodeById(id);
          setCurrent(h);
          setForm({
            hsnCode: h.hsnCode,
            description: h.description || '',
            chapter: h.chapter || '',
            heading: h.heading || '',
            subHeading: h.subHeading || '',
            currentIgstRate: h.currentIgstRate || 0,
            currentCgstRate: h.currentCgstRate || 0,
            currentSgstRate: h.currentSgstRate || 0,
            currentCessRate: h.currentCessRate || 0,
            isActive: h.isActive ?? true,
            branchId: h.branchId || selectedBranch || 0,
          });
          
          // Fetch tax rate history
          if (h.id) {
            await loadTaxRateHistory(h.id);
          }
          
          // Set default effectiveFrom to today if not already set
          if (!effectiveFrom) {
            setEffectiveFrom(new Date().toISOString().split('T')[0]);
          }
        } else {
          // Set branchId for new HSN code
          setForm(prev => ({ ...prev, branchId: selectedBranch || 0 }));
          // Set default effectiveFrom to today
          setEffectiveFrom(new Date().toISOString().split('T')[0]);
        }
      } catch (e) {
        // already toasted in services
      }
    };
    init();
  }, [id, selectedBranch]);

  const handleChange = (key: keyof HsnCodeCreateRequest, value: string | number | boolean | undefined) => {
    setForm(prev => ({ ...prev, [key]: value } as HsnCodeCreateRequest));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.hsnCode) {
      toast.error('Please fill in HSN Code');
      return;
    }
    if (!effectiveFrom) {
      toast.error('Please fill in Effective From date');
      return;
    }
    
    // Validate that effectiveFrom doesn't match any existing Tax Rate History entry
    if (current && current.id && taxRateHistory.length > 0) {
      const selectedDate = new Date(effectiveFrom).toDateString();
      const isDuplicate = taxRateHistory.some(history => {
        if (history.effectiveFrom) {
          const historyDate = new Date(history.effectiveFrom).toDateString();
          return historyDate === selectedDate;
        }
        return false;
      });
      
      if (isDuplicate) {
        toast.error('The selected Effective From date already exists in Tax Rate History. Please choose a different date.');
        return;
      }
    }
    
    setLoading(true);
    try {
      const payload = {
        ...form,
        taxRateEffectiveFrom: effectiveFrom ? new Date(effectiveFrom).toISOString() : undefined,
        taxRateChangeReason: taxRateChangeReason || undefined,
        taxRateChangedBy: user?.username || user?.displayName || 'System',
      };
      
      if (current && current.id) {
        await hsnApiService.updateHsnCode(String(current.id), { ...payload, id: current.id });
        // Toast is handled in the API service
      } else {
        await hsnApiService.createHsnCode(payload);
        // Toast is handled in the API service
      }
      
      // Tax rate history is handled by the backend automatically
      
      navigate('/hsn');
    } catch (e) {
      // Errors are handled and toasted in the API service
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate('/hsn');

  const openAddHistoryDialog = () => {
    setNewHistoryForm({
      effectiveFrom: new Date().toISOString().split('T')[0],
      igstRate: form.currentIgstRate || 0,
      cgstRate: form.currentCgstRate || 0,
      sgstRate: form.currentSgstRate || 0,
      cessRate: form.currentCessRate || 0,
      reasonForChange: '',
    });
    setAddHistoryOpen(true);
  };

  const handleAddTaxHistory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!current?.id) return;
    if (!newHistoryForm.effectiveFrom) {
      toast.error('Please select Effective From date');
      return;
    }

    const selectedDate = new Date(newHistoryForm.effectiveFrom).toDateString();
    const isDuplicate = taxRateHistory.some((history) => {
      if (history.effectiveFrom) {
        return new Date(history.effectiveFrom).toDateString() === selectedDate;
      }
      return false;
    });
    if (isDuplicate) {
      toast.error('A tax rate entry already exists for this Effective From date.');
      return;
    }

    setSavingHistory(true);
    try {
      await taxRateHistoryApiService.createTaxRateHistory({
        hsn: { id: current.id, hsnCode: form.hsnCode },
        igstRate: newHistoryForm.igstRate,
        cgstRate: newHistoryForm.cgstRate,
        sgstRate: newHistoryForm.sgstRate,
        cessRate: newHistoryForm.cessRate,
        effectiveFrom: new Date(newHistoryForm.effectiveFrom).toISOString(),
        reasonForChange: newHistoryForm.reasonForChange.trim() || undefined,
        changedBy: user?.username || user?.displayName || 'System',
      });
      setAddHistoryOpen(false);
      await loadTaxRateHistory(current.id);
      if (id) {
        await refreshHsnCurrentRates(id);
      }
    } catch {
      // Error toasted in service
    } finally {
      setSavingHistory(false);
    }
  };

  return (
    <Card className="w-full border-0 shadow-lg bg-white/70 backdrop-blur-sm">
      <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <CardTitle className="text-xl text-gray-900">
              {viewMode ? 'View HSN Code' : current ? 'Edit HSN Code' : 'Add New HSN Code'}
            </CardTitle>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6">
        <form onSubmit={handleSave} className="space-y-8">
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                Basic Information
              </h3>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>HSN Code *</Label>
                <Input
                  value={form.hsnCode}
                  onChange={(e) => handleChange('hsnCode', e.target.value)}
                  placeholder="Enter HSN code"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={viewMode}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Enter description"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={viewMode}
                />
              </div>
              <div className="space-y-2">
                <Label>Chapter</Label>
                <Input
                  value={form.chapter}
                  onChange={(e) => handleChange('chapter', e.target.value)}
                  placeholder="Enter chapter"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={viewMode}
                  maxLength={10}
                />
              </div>
              <div className="space-y-2">
                <Label>Heading</Label>
                <Input
                  value={form.heading}
                  onChange={(e) => handleChange('heading', e.target.value)}
                  placeholder="Enter heading"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={viewMode}
                  maxLength={10}
                />
              </div>
              <div className="space-y-2">
                <Label>Sub Heading</Label>
                <Input
                  value={form.subHeading}
                  onChange={(e) => handleChange('subHeading', e.target.value)}
                  placeholder="Enter sub heading"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={viewMode}
                  maxLength={10}
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="isActive"
                      checked={form.isActive === true}
                      onChange={() => handleChange('isActive', true)}
                      disabled={viewMode}
                      className="text-primary-600 focus:ring-primary-400"
                    />
                    <span>Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="isActive"
                      checked={form.isActive === false}
                      onChange={() => handleChange('isActive', false)}
                      disabled={viewMode}
                      className="text-primary-600 focus:ring-primary-400"
                    />
                    <span>Inactive</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                Tax Rates
              </h3>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>IGST Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.currentIgstRate === 0 ? '' : form.currentIgstRate}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleChange('currentIgstRate', val === '' ? 0 : parseFloat(val) || 0);
                  }}
                  placeholder="0.00"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={viewMode}
                />
              </div>
              <div className="space-y-2">
                <Label>CGST Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.currentCgstRate === 0 ? '' : form.currentCgstRate}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleChange('currentCgstRate', val === '' ? 0 : parseFloat(val) || 0);
                  }}
                  placeholder="0.00"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={viewMode}
                />
              </div>
              <div className="space-y-2">
                <Label>SGST Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.currentSgstRate === 0 ? '' : form.currentSgstRate}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleChange('currentSgstRate', val === '' ? 0 : parseFloat(val) || 0);
                  }}
                  placeholder="0.00"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={viewMode}
                />
              </div>
              <div className="space-y-2">
                <Label>CESS Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.currentCessRate === 0 ? '' : form.currentCessRate}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleChange('currentCessRate', val === '' ? 0 : parseFloat(val) || 0);
                  }}
                  placeholder="0.00"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={viewMode}
                />
              </div>
              {!viewMode && (
                <div className="space-y-2">
                  <Label>Effective From {!current ? '*' : ''}</Label>
                  <Input
                    type="date"
                    value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)}
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    disabled={viewMode}
                    required={!current}
                  />
                </div>
              )}
              {!viewMode && current && (
                <div className="space-y-2">
                  <Label>Tax Rate Change Reason</Label>
                  <Input
                    value={taxRateChangeReason}
                    onChange={(e) => setTaxRateChangeReason(e.target.value)}
                    placeholder="Enter reason for tax rate change"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    disabled={viewMode}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Tax Rate History Section - Only show when editing */}
          {current && current.id && (
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <History className="h-5 w-5 text-primary-600" />
                    <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                      Tax Rate History
                    </h3>
                  </div>
                  {/* {!viewMode && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={openAddHistoryDialog}
                      className="border-primary-200 text-primary-700 hover:bg-primary-50"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Tax History
                    </Button>
                  )} */}
                </div>
                <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
              </div>

              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                    <span>Loading tax rate history...</span>
                  </div>
                </div>
              ) : taxRateHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No tax rate history available
                  {!viewMode && (
                    <p className="text-sm mt-2">Use &quot;Add Tax History&quot; to create the first entry.</p>
                  )}
                </div>
              ) : (
                <div className="border border-primary-200 rounded-lg overflow-hidden bg-white">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-primary-50">
                        <TableHead className="font-semibold text-primary-800">Effective From</TableHead>
                        <TableHead className="font-semibold text-primary-800">IGST (%)</TableHead>
                        <TableHead className="font-semibold text-primary-800">CGST (%)</TableHead>
                        <TableHead className="font-semibold text-primary-800">SGST (%)</TableHead>
                        <TableHead className="font-semibold text-primary-800">CESS (%)</TableHead>
                        <TableHead className="font-semibold text-primary-800">Changed By</TableHead>
                        <TableHead className="font-semibold text-primary-800">Reason</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {taxRateHistory.map((history) => (
                        <TableRow key={history.id} className="hover:bg-primary-50/50">
                          <TableCell>
                            {history.effectiveFrom ? new Date(history.effectiveFrom).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>{history.igstRate?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>{history.cgstRate?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>{history.sgstRate?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>{history.cessRate?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>{history.changedBy || '-'}</TableCell>
                          <TableCell className="max-w-[200px] truncate" title={history.reasonForChange || ''}>
                            {history.reasonForChange || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          <Dialog open={addHistoryOpen} onOpenChange={setAddHistoryOpen}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Add Tax Rate History</DialogTitle>
                <DialogDescription>
                  Add a new tax rate effective from a specific date for HSN {form.hsnCode}.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddTaxHistory} className="space-y-4">
                <div className="space-y-2">
                  <Label>Effective From *</Label>
                  <Input
                    type="date"
                    value={newHistoryForm.effectiveFrom}
                    onChange={(e) =>
                      setNewHistoryForm((prev) => ({ ...prev, effectiveFrom: e.target.value }))
                    }
                    required
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>IGST Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={newHistoryForm.igstRate === 0 ? '' : newHistoryForm.igstRate}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewHistoryForm((prev) => ({
                          ...prev,
                          igstRate: val === '' ? 0 : parseFloat(val) || 0,
                        }));
                      }}
                      placeholder="0.00"
                      className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>CGST Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={newHistoryForm.cgstRate === 0 ? '' : newHistoryForm.cgstRate}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewHistoryForm((prev) => ({
                          ...prev,
                          cgstRate: val === '' ? 0 : parseFloat(val) || 0,
                        }));
                      }}
                      placeholder="0.00"
                      className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SGST Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={newHistoryForm.sgstRate === 0 ? '' : newHistoryForm.sgstRate}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewHistoryForm((prev) => ({
                          ...prev,
                          sgstRate: val === '' ? 0 : parseFloat(val) || 0,
                        }));
                      }}
                      placeholder="0.00"
                      className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>CESS Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={newHistoryForm.cessRate === 0 ? '' : newHistoryForm.cessRate}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewHistoryForm((prev) => ({
                          ...prev,
                          cessRate: val === '' ? 0 : parseFloat(val) || 0,
                        }));
                      }}
                      placeholder="0.00"
                      className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Reason</Label>
                  <Input
                    value={newHistoryForm.reasonForChange}
                    onChange={(e) =>
                      setNewHistoryForm((prev) => ({ ...prev, reasonForChange: e.target.value }))
                    }
                    placeholder="Enter reason for tax rate change"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  />
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAddHistoryOpen(false)}
                    disabled={savingHistory}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={savingHistory}>
                    {savingHistory ? 'Saving...' : 'Add Entry'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {!viewMode && (
            <div className="flex justify-end gap-4 pt-6 border-t border-primary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="border-primary-200 text-primary-600 hover:bg-primary-50"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
                disabled={loading}
              >
                {loading ? 'Saving...' : (current ? 'Update HSN Code' : 'Create HSN Code')}
              </Button>
            </div>
          )}

          {viewMode && (
            <div className="flex justify-end gap-4 pt-6 border-t border-primary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="border-primary-200 text-primary-600 hover:bg-primary-50"
              >
                Close
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

